const {
  ABOUT_REGEX,
  ACCOUNT_REGEX,
  ARCHIVE_REGEX,
  AUTH_REGEX,
  AUTHOR_REGEX,
  CATEGORY_REGEX,
  COLLECTION_REGEX,
  CONTESTS_REGEX,
  FAQ_REGEX,
  FEATURES_REGEX,
  HOMEMADE_REGEX,
  POST_REGEX,
  PRISM_REGEX,
  SEARCH_REGEX,
  SHARED_REGEX,
  SMALL_COOL_EXPERIENCE_REGEX,
  SUBMISSIONS_REGEX
} = require('../constants/regex');

const {
  SITE_KEY,
  LONGTAIL_POST_CUTOFF_ID,
  SITE_WIDE_A_B_FLIPS
} = require('../constants/site');

function getPageType(path) {
  if (path === '/') {
    return 'home';
  } else if (ARCHIVE_REGEX.test(path)) {
    return 'archive';
  } else if (COLLECTION_REGEX.test(path)) {
    return 'collection';
  } else if (CATEGORY_REGEX.test(path)) {
    return 'category';
  } else if (POST_REGEX.test(path)) {
    return 'posts';
  } else if (ABOUT_REGEX.test(path)) {
    return 'about';
  } else if (SEARCH_REGEX.test(path)) {
    return 'search';
  } else if (PRISM_REGEX.test(path)) {
    return 'prism';
  } else if (AUTHOR_REGEX.test(path)) {
    return 'authors';
  } else if (FEATURES_REGEX.test(path)) {
    return 'features';
  } else if (HOMEMADE_REGEX.test(path)) {
    return 'homemade';
  } else if (FAQ_REGEX.test(path)) {
    return 'faq';
  } else if (SMALL_COOL_EXPERIENCE_REGEX.test(path)) {
    return 'small-cool-experience';
  } else if (CONTESTS_REGEX.test(path)) {
    return 'contests';
  } else if (SUBMISSIONS_REGEX.test(path)) {
    return 'submissions';
  } else if (AUTH_REGEX.test(path)) {
    return 'auth';
  } else if (ACCOUNT_REGEX.test(path)) {
    return 'account';
  } else if (SHARED_REGEX.test(path)) {
    return 'shared';
  }
  return null;
}

function getSurrogateKey(path, query, deployId = 'unset', flips = []) {
  const type = getPageType(path);
  const siteWideFlips = flips
    // Filter out flips that are not applied site wide. In future we _might_ have
    // a `postFlips` equivalent. Typically we apply flips on posts only, OR site-wide
    .filter(f => SITE_WIDE_A_B_FLIPS.includes(f))
    // Convert cookie name to flip formatting, e.g. flip_foo_bar, to flip:foo-bar
    .map(f => f.replace('_', ':').replace(/_/g, '-'))
    .join(' ');
  // Base keys are applied to all request that have surrogate keys applied
  const baseKeys = `deploy:${deployId} ${siteWideFlips}`.trim();

  if (!type) {
    // These assets are delivered by "genstatic" in production
    if (!path || /^\/(_next|static)\//.test(path)) {
      return '';
    }
    // Unknown page type
    return `path:${path.replace('/', '')} ${baseKeys}`;
  }

  // Convert path to array of segments
  const segments = path
    // Remove leading slash
    .replace(/^\//, '')
    // Split into array
    .split('/')
    // Encode to prevent invalid characters in header
    .map(k => encodeURI(k))
    // Remove empty strings
    .filter(v => v.length);

  let result = '';

  switch (type) {
    case 'posts': {
      // Extract the id on post pages
      const match = path.match(POST_REGEX);
      const id = parseInt(match ? match[1] : '', 10);

      // If amp is _any_ truthy value Next will trigger AMP rendering
      const mode = query && query.amp ? 'amp' : 'web';

      result = `${mode} posts posts:${SITE_KEY} posts:${mode} posts:${SITE_KEY}:${mode} posts:${id}`;

      if (id < LONGTAIL_POST_CUTOFF_ID) {
        // Old posts are considered longtail
        // Ideally we'd use publishedAt, but that will require a LOT of reworking
        // and setting surrogate keys in getInitialProps or similar.
        result = `${result} posts:longtail posts:${SITE_KEY}:longtail`;
      }
      break;
    }
    case 'collection': {
      // Collection pages require special handling so that we can add keys for
      // tag instead of path. This is done so paths such as collection/recipe/chicken
      // and collection/chicken/recipe have matching keys, i.e.
      // collection collection:k tag:recipe tag:chicken
      let tags;
      let category;

      result = `collection collection:${SITE_KEY}`;

      if (segments[0] === 'collection') {
        // This is a standard collection page, e.g. /collection/cheese
        [, ...tags] = segments;
      } else {
        // This is a collection page nested under a category, e.g. recipes/cheese
        [category, ...tags] = segments;
        result = `${result} category:${category}`;
      }
      // prettier-ignore
      result = `${result} ${tags.sort().map(s => `tag:${s}`).join(' ').trim()}`;
      break;
    }
    default: {
      let filtered;
      // If the path starts with type, e.g. "archive", remove it so it's not
      // duplicated when building the keys (we don't want archive:archive)
      if (segments[0] === type) {
        [, ...filtered] = segments;
      } else {
        filtered = segments;
      }

      const keys = filtered
        .reduce((p, c, i, a) => p.concat(`${a.slice(0, i + 1).join('/')}`), [])
        // Convert to string with each key prefixed with `type`
        .map(k => `${type}:${k}`)
        .join(' ')
        .trim();

      result = `${type} ${type}:${SITE_KEY} ${keys}`;
    }
  }
  // Append deploy id to page keys
  return `${result.trim()} ${baseKeys}`;
}

module.exports = {
  getSurrogateKey
};
